import { useCallback } from 'react';
import { useAuthContext } from './useAuthContext';
import {
  trackOpportunityView,
  trackOpportunityDetailsView,
  trackConnectionRequest,
  trackConnectionStatusChange,
  trackOpportunitySearch,
  trackOpportunityFilter,
  getOpportunityAnalytics,
  getOverallAnalytics
} from '../services/firebase/opportunity-analytics';
import {
  trackUserEngagementSession,
  trackOpportunityConversion,
  trackUserInteraction,
  getUserEngagementMetrics,
  getOpportunityPerformanceMetrics,
  getConversionSuccessMetrics
} from '../services/firebase/user-interaction-tracking';

/**
 * Custom hook for opportunity analytics tracking
 * @returns {object} Analytics tracking functions
 */
export const useOpportunityAnalytics = () => {
  const { user } = useAuthContext();
  const userId = user?.uid || null;

  // Track when a user views an opportunity card
  const trackView = useCallback(async (opportunityId, context = {}) => {
    return await trackOpportunityView(opportunityId, userId, context);
  }, [userId]);

  // Track when a user opens opportunity details
  const trackDetailsView = useCallback(async (opportunityId, context = {}) => {
    return await trackOpportunityDetailsView(opportunityId, userId, context);
  }, [userId]);

  // Track when a user submits a connection request
  const trackConnection = useCallback(async (opportunityId, requestId, context = {}) => {
    if (!userId) {
      console.warn('Cannot track connection request: user not authenticated');
      return { success: false, error: 'User not authenticated' };
    }
    return await trackConnectionRequest(opportunityId, userId, requestId, context);
  }, [userId]);

  // Track connection status changes (for admin use)
  const trackConnectionStatus = useCallback(async (requestId, newStatus, adminId, context = {}) => {
    return await trackConnectionStatusChange(requestId, newStatus, adminId, context);
  }, []);

  // Track search activity
  const trackSearch = useCallback(async (searchTerm, filters = {}, resultCount = 0, context = {}) => {
    return await trackOpportunitySearch(searchTerm, filters, resultCount, userId, context);
  }, [userId]);

  // Track filter usage
  const trackFilter = useCallback(async (appliedFilters = {}, resultCount = 0, context = {}) => {
    return await trackOpportunityFilter(appliedFilters, resultCount, userId, context);
  }, [userId]);

  // Get analytics for a specific opportunity
  const getAnalytics = useCallback(async (opportunityId, options = {}) => {
    return await getOpportunityAnalytics(opportunityId, options);
  }, []);

  // Get overall analytics summary
  const getOverallAnalyticsSummary = useCallback(async (options = {}) => {
    return await getOverallAnalytics(options);
  }, []);

  // Convenience function to track opportunity card interaction
  const trackOpportunityInteraction = useCallback(async (opportunityId, interactionType, context = {}) => {
    const baseContext = {
      timestamp: new Date().toISOString(),
      ...context
    };

    switch (interactionType) {
      case 'view':
        return await trackView(opportunityId, baseContext);
      case 'details':
        return await trackDetailsView(opportunityId, baseContext);
      default:
        console.warn(`Unknown interaction type: ${interactionType}`);
        return { success: false, error: 'Unknown interaction type' };
    }
  }, [trackView, trackDetailsView]);

  // Track user engagement session
  const trackEngagementSession = useCallback(async (sessionData) => {
    const {
      opportunitiesViewed = [],
      detailsViewed = [],
      connectionsRequested = [],
      searchesPerformed = [],
      filtersUsed = [],
      sessionDuration = null,
      source = 'web'
    } = sessionData;

    try {
      // Track all views in the session
      for (const view of opportunitiesViewed) {
        await trackView(view.opportunityId, {
          source: 'session',
          sessionId: sessionData.sessionId,
          position: view.position,
          ...view.context
        });
      }

      // Track all details views
      for (const details of detailsViewed) {
        await trackDetailsView(details.opportunityId, {
          source: 'session',
          sessionId: sessionData.sessionId,
          timeSpent: details.timeSpent,
          ...details.context
        });
      }

      // Track all connection requests
      for (const connection of connectionsRequested) {
        await trackConnection(connection.opportunityId, connection.requestId, {
          source: 'session',
          sessionId: sessionData.sessionId,
          ...connection.context
        });
      }

      // Track searches
      for (const search of searchesPerformed) {
        await trackSearch(
          search.searchTerm,
          search.filters,
          search.resultCount,
          {
            sessionId: sessionData.sessionId,
            searchDuration: search.duration,
            ...search.context
          }
        );
      }

      // Track filter usage
      for (const filter of filtersUsed) {
        await trackFilter(
          filter.appliedFilters,
          filter.resultCount,
          {
            sessionId: sessionData.sessionId,
            ...filter.context
          }
        );
      }

      return { success: true };
    } catch (error) {
      console.error('Error tracking engagement session:', error);
      return { success: false, error: error.message };
    }
  }, [trackView, trackDetailsView, trackConnection, trackSearch, trackFilter]);

  // Track user interaction with opportunity (clicks, hovers, shares, bookmarks)
  const trackInteraction = useCallback(async (opportunityId, interactionType, context = {}) => {
    return await trackUserInteraction(opportunityId, userId, interactionType, context);
  }, [userId]);

  // Track opportunity conversion events
  const trackConversion = useCallback(async (opportunityId, conversionType, context = {}) => {
    if (!userId) {
      console.warn('Cannot track conversion: user not authenticated');
      return { success: false, error: 'User not authenticated' };
    }
    return await trackOpportunityConversion(opportunityId, userId, conversionType, context);
  }, [userId]);

  // Track user engagement session with enhanced data
  const trackUserSession = useCallback(async (sessionData) => {
    if (!userId) {
      console.warn('Cannot track user session: user not authenticated');
      return { success: false, error: 'User not authenticated' };
    }
    return await trackUserEngagementSession(userId, sessionData);
  }, [userId]);

  // Get user engagement metrics
  const getUserEngagement = useCallback(async (options = {}) => {
    if (!userId) {
      console.warn('Cannot get user engagement: user not authenticated');
      return { success: false, error: 'User not authenticated' };
    }
    return await getUserEngagementMetrics(userId, options);
  }, [userId]);

  // Get opportunity performance metrics
  const getOpportunityPerformance = useCallback(async (opportunityId, options = {}) => {
    return await getOpportunityPerformanceMetrics(opportunityId, options);
  }, []);

  // Get conversion success metrics
  const getConversionMetrics = useCallback(async (options = {}) => {
    return await getConversionSuccessMetrics(options);
  }, []);

  return {
    // Core tracking functions
    trackView,
    trackDetailsView,
    trackConnection,
    trackConnectionStatus,
    trackSearch,
    trackFilter,
    
    // Enhanced tracking functions
    trackInteraction,
    trackConversion,
    
    // Convenience functions
    trackOpportunityInteraction,
    trackEngagementSession,
    trackUserSession,
    
    // Analytics retrieval
    getAnalytics,
    getOverallAnalyticsSummary,
    getUserEngagement,
    getOpportunityPerformance,
    getConversionMetrics,
    
    // User context
    userId,
    isAuthenticated: !!userId
  };
};